/**
 * DV Copilot · o assistente conversacional do DigitalView pelo WhatsApp.
 *
 * O Copilot não é um sistema de regras novo: ele entende a pergunta, escolhe uma consulta de um
 * catálogo fechado e responde com o que o próprio DigitalView devolveu, com as permissões de quem
 * perguntou. O que este arquivo define é o que é PRÓPRIO do canal conversacional: a adesão (separada
 * da adesão às notificações da D-204), os limites, os eventos de entrega e o formato do webhook do WAHA
 * que a API aceita. Nenhum telefone, texto de conversa ou credencial viaja em job.
 */
import { z } from 'zod'
import { uuid } from './comum.js'

/** O turno de conversa de uma pessoa: o job carrega só quem, e o consumidor relê as mensagens pendentes. */
export const FILA_COPILOT_TURNO = 'dv-copilot-turno'
/** A limpeza diária dos prazos de retenção (contexto, entrada, evidência, auditoria). */
export const FILA_COPILOT_LIMPEZA = 'dv-copilot-limpeza'
/** O resumo proativo do dia, para quem ligou essa preferência. */
export const FILA_COPILOT_RESUMO = 'dv-copilot-resumo'

export const jobDeTurnoDoCopilot = z.object({ usuarioId: uuid }).strict()
export type JobDeTurnoDoCopilot = z.infer<typeof jobDeTurnoDoCopilot>

/** Os eventos de `whatsapp_entrega` que são do Copilot: não pertencem ao catálogo de notificações. */
export const EVENTO_COPILOT_RESPOSTA = 'copilot_resposta'
export const EVENTO_COPILOT_RESUMO = 'copilot_resumo'
/**
 * O aviso FIXO do Copilot, sem dado nenhum: a confirmação do "parar" e o convite "ative no Meu perfil". É o
 * único evento do Copilot que sai sem adesão ao Copilot vigente (ela acabou de ser revogada, ou nunca existiu);
 * continua exigindo o WhatsApp verificado e consentido da D-204.
 */
export const EVENTO_COPILOT_AVISO = 'copilot_aviso'
export const EVENTOS_DO_COPILOT = [EVENTO_COPILOT_RESPOSTA, EVENTO_COPILOT_RESUMO, EVENTO_COPILOT_AVISO] as const
export function ehEventoDoCopilot(evento: string): boolean {
  return (EVENTOS_DO_COPILOT as readonly string[]).includes(evento)
}

/** O cabeçalho que o WAHA assina (HMAC SHA-512 do corpo bruto, em hexadecimal). */
export const CABECALHO_HMAC_DO_WAHA = 'x-webhook-hmac'
/** O caminho interno do webhook. Fica fora do Caddy: o WAHA o alcança pela rede dos contêineres. */
export const CAMINHO_DO_WEBHOOK_DO_COPILOT = '/interno/copilot/waha'

/**
 * Os limites técnicos. Nenhum é regra comercial: protegem o canal, o custo e a pessoa de uma rajada.
 */
export const LIMITES_DO_COPILOT = {
  /** Turnos aceitos por pessoa numa hora; acima, a mensagem recebe a resposta fixa de limite. */
  turnosPorPessoaPorHora: 30,
  /** Turnos aceitos por pessoa num dia. */
  turnosPorPessoaPorDia: 150,
  /** Mensagens esperando a vez de uma pessoa; acima, a mais nova é descartada sem conteúdo (evento `limite`, sem resposta). */
  filaMaximaPorPessoa: 3,
  /** Rodadas de consulta por turno (a IA pede, o sistema responde) antes do modo simples. */
  rodadasDeFerramenta: 3,
  /** Consultas por turno, somadas as rodadas. */
  chamadasPorTurno: 6,
  /** Contexto conversacional (foco, período, filtros, referências) expira sem interação. */
  ttlDoContextoMinutos: 30,
  /** A escolha numerada pendente (desambiguação, troca de entidade). */
  validadeDaEscolhaMinutos: 5,
  /** A confirmação de uma ação (criar tarefa, criar lembrete). */
  validadeDaConfirmacaoMinutos: 5,
  /** Janela anti-replay do webhook. */
  janelaDoWebhookSegundos: 300,
  /** Áudio: duração máxima aceita. */
  audioMaximoSegundos: 120,
  /** Áudio: tamanho máximo (o limite do provedor de transcrição). */
  audioMaximoBytes: 25 * 1024 * 1024,
  /** Itens por lista na resposta. */
  itensPorLista: 5,
  /** Sugestões de continuação por resposta. */
  sugestoesMaximas: 2,
  /** Tamanho máximo da resposta no aparelho. */
  caracteresMaximos: 2_000,
  /** Tamanho máximo do texto recebido que entra no turno. */
  caracteresDaPergunta: 1_000,
  /** A resposta que não saiu nesse prazo ficou velha e não sai. */
  validadeDaRespostaMinutos: 10,
  /** O resumo do dia vale a manhã: o worker atrasado ainda o entrega até as 09:45 de São Paulo. */
  validadeDoResumoMinutos: 120,
  /** Respostas do Copilot por pessoa numa hora (orçamento próprio, fora do das notificações). */
  respostasPorPessoaPorHora: 60,
  /** O convite "ative no Meu perfil" para quem escreve sem ter aderido: no máximo um por dia. */
  convitesPorDia: 1,
  /** Retenção: a entrada bruta de um turno que falhou. */
  retencaoDaEntradaComFalhaHoras: 24,
  /** Retenção: o id da mensagem recebida, só para o dedupe. */
  retencaoDoDedupeDias: 7,
  /** Retenção: a evidência técnica resumida (projeção, termos, esqueleto da resposta). */
  retencaoDaEvidenciaDias: 30,
  /** Retenção: a auditoria mínima (turno, consulta, veredito) e os eventos de segurança. */
  retencaoDaAuditoriaDias: 90,
} as const

/**
 * O interruptor do Copilot (`DV_COPILOT_ATIVO`), com a mesma regra do canal: ausente, vazio ou `false`
 * desliga; `true` liga; outro valor é erro de configuração. Desligado, o webhook ignora tudo e o worker
 * não entrega resposta do Copilot; as notificações não mudam.
 */
export function copilotAtivo(valor: string | undefined): boolean {
  const v = valor?.trim().toLocaleLowerCase('en-US')
  if (v === undefined || v === '' || v === 'false') return false
  if (v === 'true') return true
  throw new Error('DV_COPILOT_ATIVO deve ser true ou false.')
}

/**
 * A liberação gradual (`DV_COPILOT_LIBERADOS`): ids de `usuario_interno` separados por vírgula, ou `*`.
 * Ausente ou vazia: ninguém. É a segunda chave, além do interruptor, e vale na API e no worker.
 */
export function copilotLiberadoPara(lista: string | undefined, usuarioId: string): boolean {
  const itens = (lista ?? '').split(',').map((x) => x.trim().toLowerCase()).filter((x) => x !== '')
  return itens.includes('*') || itens.includes(usuarioId.toLowerCase())
}

/** A versão do texto do consentimento: muda o texto, muda a versão, e a adesão antiga fica registrada como era. */
export const VERSAO_DO_CONSENTIMENTO_COPILOT = 'copilot-v1-2026-11-02'

export const TEXTO_DO_CONSENTIMENTO_COPILOT =
  'Autorizo o DV Copilot a receber as mensagens de texto e de voz que eu enviar ao WhatsApp do DigitalView '
  + 'neste número e a responder com informações do sistema, sempre dentro das permissões do meu acesso, '
  + 'inclusive valores financeiros quando o meu acesso permitir. As mensagens são interpretadas por um serviço '
  + 'de inteligência artificial contratado pelo DigitalView, e as respostas ficam também guardadas no meu WhatsApp '
  + 'e no meu aparelho. Posso desativar a qualquer momento nesta tela ou enviando "parar".'

/**
 * Onde a pessoa está, para a 2.3 dizer a coisa certa:
 *  - `indisponivel`: o Copilot está desligado no servidor;
 *  - `nao_liberado`: ligado, mas a liberação gradual ainda não inclui esta pessoa;
 *  - `sem_whatsapp`: falta o WhatsApp verificado (a verificação do telefone é a da D-204);
 *  - `inativo`: verificado, sem adesão ao Copilot (nunca aderiu ou desativou);
 *  - `ativo`: adesão válida para o telefone verificado de agora.
 */
export const SITUACOES_DO_COPILOT = ['indisponivel', 'nao_liberado', 'sem_whatsapp', 'inativo', 'ativo'] as const
export const situacaoDoCopilot = z.enum(SITUACOES_DO_COPILOT)
export type SituacaoDoCopilot = z.infer<typeof situacaoDoCopilot>

export const estadoDoCopilot = z.object({
  situacao: situacaoDoCopilot,
  /** Os quatro últimos dígitos do número verificado. Nunca o número inteiro. */
  finalDoTelefone: z.string().regex(/^\d{4}$/).nullable(),
  ativadoEm: z.string().nullable(),
  desativadoEm: z.string().nullable(),
  /** `perfil` (Meu perfil), `whatsapp` (o comando "parar") ou `sistema` (telefone trocado ou verificação desfeita). */
  desativadoPor: z.enum(['perfil', 'whatsapp', 'sistema']).nullable(),
  resumoDiario: z.boolean(),
  textoDoConsentimento: z.string(),
  versaoDoConsentimento: z.string(),
})
export type EstadoDoCopilot = z.infer<typeof estadoDoCopilot>

export const entradaDeAdesaoDoCopilot = z.object({
  consentimento: z.literal(true, { error: 'Marque a autorização para usar o DV Copilot.' }),
  versaoDoConsentimento: z.literal(VERSAO_DO_CONSENTIMENTO_COPILOT, {
    error: 'O texto da autorização mudou. Recarregue a página e leia de novo.',
  }),
}).strict()
export type EntradaDeAdesaoDoCopilot = z.infer<typeof entradaDeAdesaoDoCopilot>

export const entradaDePreferenciasDoCopilot = z.object({ resumoDiario: z.boolean() }).strict()
export type EntradaDePreferenciasDoCopilot = z.infer<typeof entradaDePreferenciasDoCopilot>

/**
 * O pedaço do evento `message` do WAHA que a API lê. O WAHA acrescenta campos entre versões, então o
 * objeto é aberto; o que está aqui é validado, e o que não está nunca é lido.
 */
export const midiaDoWaha = z.object({
  url: z.string().nullable().optional(),
  mimetype: z.string().nullable().optional(),
  filename: z.string().nullable().optional(),
}).loose()

export const mensagemDoWaha = z.object({
  id: z.string().min(1).max(300),
  timestamp: z.number().int().nonnegative(),
  from: z.string().min(1).max(200),
  fromMe: z.boolean(),
  source: z.string().optional(),
  body: z.string().nullable().optional(),
  hasMedia: z.boolean().optional(),
  media: midiaDoWaha.nullable().optional(),
  replyTo: z.object({ id: z.string().nullable().optional() }).loose().nullable().optional(),
  _data: z.unknown().optional(),
}).loose()
export type MensagemDoWaha = z.infer<typeof mensagemDoWaha>

export const eventoDoWaha = z.object({
  event: z.string(),
  session: z.string(),
  payload: z.unknown(),
}).loose()
export type EventoDoWaha = z.infer<typeof eventoDoWaha>
